import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  BranchDto,
  CreditNoteRow,
  CreditNotesReport,
  SalesReturnRow,
  SalesReturnsReport,
  WalletBalanceRow,
  WalletMovementRow,
  ReportExportColumn,
  ReportExportRequest,
  WalletReport,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { Badge } from '@/components/Badge';
import type { BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ExportMenu } from '@/components/ExportMenu';
import { KpiCard } from '@/components/KpiCard';
import { Modal } from '@/components/Modal';
import {
  fetchCreditNotes,
  fetchSalesReturns,
  fetchWallet,
  returnsWalletKeys,
} from '@/features/reports/returns-wallet-api';
import { downloadReportExport, type ExportFormat } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import type { ReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import type { UseReportToolbar } from '@/features/reports/useReportToolbar';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
/** Decimal quantities (weighed goods) — trailing zeros dropped, as the POS's `Formatters.qty`. */
const qty = (value: number) => String(Number(value.toFixed(3)));

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

const dash = (value: string | null | undefined) => (value ? value : '—');

type Tab = 'returns' | 'notes' | 'wallet';

const TABS: { id: Tab; label: string }[] = [
  { id: 'returns', label: 'Sales returns' },
  { id: 'notes', label: 'Credit notes' },
  { id: 'wallet', label: 'Customer wallet' },
];

function isTab(value: string | null): value is Tab {
  return value === 'returns' || value === 'notes' || value === 'wallet';
}

// ---------------------------------------------------------------- Export

/** Written to the spreadsheet as currency numbers. */
const EXPORT_MONEY = new Set([
  'refundAmount', 'amount', 'netAmount', 'taxAmount', 'againstDue', 'totalIn', 'used', 'returned', 'refund', 'balance',
]);

/** '2026-10-01' → '01 Oct 2026', read as a calendar date rather than UTC midnight. */
function readableDate(ymd: string): string {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return ymd;
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "01 Oct 2026 – 16 Oct 2026 · Main branch" — an empty range is the API's 30-day default. */
function exportSubtitle(filters: ReportFilters, branches: readonly BranchDto[], extra: string[] = []): string {
  const range =
    filters.from && filters.to ? `${readableDate(filters.from)} – ${readableDate(filters.to)}` : 'Last 30 days';
  const branch = branches.find((option) => option.id === filters.branchId)?.name ?? 'All branches';
  const search = filters.search.trim() ? `Matching "${filters.search.trim()}"` : '';
  return [range, branch, search, ...extra].filter(Boolean).join(' · ');
}

/**
 * One table as an export — the columns the user's layout shows, in its
 * order. `text` overrides the cells that render markup on screen; the
 * first (pinned) column carries the totals label, so `totals` skips it.
 */
function tabTable<T>(
  title: string,
  subtitle: string,
  filters: ReportFilters,
  columns: readonly Column<T>[],
  rows: readonly T[],
  totals: Record<string, string> | null,
  text: Partial<Record<string, (row: T) => string>> = {},
): ReportExportRequest {
  const exportColumns: ReportExportColumn[] = columns.map((column) => ({
    header: column.header,
    ...(column.align === 'right' ? { align: 'right' as const } : {}),
    ...(EXPORT_MONEY.has(column.key) ? { money: true } : {}),
  }));
  return {
    title,
    subtitle,
    from: filters.from,
    to: filters.to,
    columns: exportColumns,
    rows: rows.map((row) => columns.map((column) => text[column.key]?.(row) ?? String(column.cell(row)))),
    ...(totals ? { totals: { label: 'Total', cells: columns.slice(1).map((column) => totals[column.key] ?? '') } } : {}),
  };
}

// ---------------------------------------------------------------- Sales returns

const RETURN_FILTER_FIELDS = ['Customer', 'Sale', 'Reason', 'Refund method'] as const;
const RETURN_TOGGLEABLE = ['date', 'customerName', 'saleDocNumber', 'reason', 'refundMethod', 'refundAmount', 'items'] as const;
const RETURN_LABELS: Record<string, string> = {
  date: 'Date',
  customerName: 'Customer',
  saleDocNumber: 'Sale',
  reason: 'Reason',
  refundMethod: 'Refund method',
  refundAmount: 'Refund',
  items: 'Items',
};

function returnColumns(onView: (row: SalesReturnRow) => void): Column<SalesReturnRow>[] {
  return [
    { key: 'returnNumber', header: 'Return #', pinned: true, cell: (row) => row.returnNumber },
    { key: 'date', header: 'Date', cell: (row) => formatDate(row.date) },
    { key: 'customerName', header: 'Customer', cell: (row) => dash(row.customerName) },
    { key: 'saleDocNumber', header: 'Sale', cell: (row) => dash(row.saleDocNumber) },
    { key: 'reason', header: 'Reason', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.reason) },
    { key: 'refundMethod', header: 'Refund method', cell: (row) => dash(row.refundMethod) },
    { key: 'refundAmount', header: 'Refund', align: 'right', cell: (row) => money(row.refundAmount) },
    {
      key: 'items',
      header: 'Items',
      cell: (row) => (
        <Button size="sm" variant="ghost" onClick={() => onView(row)} aria-label={`View items of ${row.returnNumber}`}>
          {row.items.length} {row.items.length === 1 ? 'item' : 'items'}
        </Button>
      ),
    },
  ];
}

const ITEM_COLUMNS: Column<SalesReturnRow['items'][number]>[] = [
  { key: 'productName', header: 'Product', cell: (row) => row.productName },
  { key: 'quantity', header: 'Qty', align: 'right', cell: (row) => qty(row.quantity) },
  { key: 'unitPrice', header: 'Unit price', align: 'right', cell: (row) => money(row.unitPrice) },
  { key: 'total', header: 'Total', align: 'right', cell: (row) => money(row.total) },
];

function SalesReturnsTab({ filters, toolbar }: { filters: ReportFilters; toolbar: UseReportToolbar }) {
  const { profile } = usePortal();
  const layout = useReportLayout('sales-returns', RETURN_TOGGLEABLE);
  const [viewing, setViewing] = useState<SalesReturnRow | null>(null);
  const columns = applyReportLayout(returnColumns(setViewing), layout);
  const query = useQuery({
    queryKey: returnsWalletKeys.salesReturns(filters),
    queryFn: ({ signal }) => fetchSalesReturns(filters, signal),
  });
  const narrow = (report: SalesReturnsReport) => {
    const rows = applyClientFilters(report.rows, {
      search: toolbar.search,
      filterRows: toolbar.filterRows,
      matchAny: toolbar.matchAny,
      fieldValue: (row, field) =>
        field === 'Customer'
          ? row.customerName
          : field === 'Sale'
            ? (row.saleDocNumber ?? '')
            : field === 'Reason'
              ? row.reason
              : field === 'Refund method'
                ? row.refundMethod
                : undefined,
      searchValues: (row) => [row.returnNumber, row.customerName, row.saleDocNumber ?? '', row.reason],
    });
    return { rows, count: rows.length, totalRefund: rows.reduce((acc, row) => acc + row.refundAmount, 0) };
  };
  // The rows on screen, also outside AsyncSection so Export sends exactly them.
  const shown = query.data ? narrow(query.data) : null;

  return (
    <>
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search return #, customer, sale or reason"
        />
        <ReportFiltersButton
          fields={RETURN_FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) => (field === 'Refund method' ? 'Cash / Card' : field.toLowerCase())}
        />
        <ReportColumnsButton layout={layout} labels={RETURN_LABELS} />
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} deselected={toolbar.filterActive} />
        {shown && shown.rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu
              onExport={(format: ExportFormat) =>
                downloadReportExport(
                  tabTable(
                    'Sales Returns',
                    exportSubtitle(filters, profile?.branches ?? []),
                    filters,
                    columns,
                    shown.rows,
                    { refundAmount: money(shown.totalRefund) },
                    { items: (row) => `${row.items.length} ${row.items.length === 1 ? 'item' : 'items'}` },
                  ),
                  format,
                )
              }
            />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        select={narrow}
        isEmpty={(report) => report.rows.length === 0}
        emptyTitle="No sales returns in this window"
        skeleton={<TableSkeleton columns={8} />}
      >
        {(report) => (
          <>
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <KpiCard label="Returns" value={String(report.count)} />
              <KpiCard label="Refunded" value={money(report.totalRefund)} />
            </div>
            <DataTable
              caption="Sales returns"
              layout={layout}
              columns={columns}
              rows={report.rows}
              rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
              totals={{ label: 'Total', cells: { refundAmount: money(report.totalRefund) } }}
            />
          </>
        )}
      </AsyncSection>
      <Modal
        open={viewing !== null}
        title={viewing ? `Return ${viewing.returnNumber}` : ''}
        description={
          viewing
            ? `${formatDate(viewing.date)} · ${dash(viewing.customerName)} · against ${dash(viewing.saleDocNumber)}`
            : undefined
        }
        onClose={() => setViewing(null)}
        size="lg"
      >
        {viewing ? (
          viewing.items.length === 0 ? (
            <p className="text-sm text-slate-500">No item lines synced for this return.</p>
          ) : (
            <DataTable
              caption={`Items returned on ${viewing.returnNumber}`}
              columns={ITEM_COLUMNS}
              rows={viewing.items}
              rowKey={(row, index) => `${row.productName}-${index}`}
              totals={{ label: 'Refund amount', cells: { total: money(viewing.refundAmount) } }}
            />
          )
        ) : null}
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------- Credit notes

const NOTE_FILTER_FIELDS = ['Customer', 'Sale', 'Reason', 'Refund method', 'By'] as const;
const NOTE_TOGGLEABLE = [
  'date', 'customerName', 'saleDocNumber', 'amount', 'netAmount', 'taxAmount', 'againstDue',
  'refundAmount', 'refundMethod', 'reason', 'createdBy',
] as const;
const NOTE_LABELS: Record<string, string> = {
  date: 'Date',
  customerName: 'Customer',
  saleDocNumber: 'Sale',
  amount: 'Amount',
  netAmount: 'Net',
  taxAmount: 'VAT',
  againstDue: 'Off balance',
  refundAmount: 'Refunded',
  refundMethod: 'Method',
  reason: 'Reason',
  createdBy: 'By',
};

const NOTE_COLUMNS: Column<CreditNoteRow>[] = [
  { key: 'noteNumber', header: 'Note #', pinned: true, cell: (row) => row.noteNumber },
  { key: 'date', header: 'Date', cell: (row) => formatDate(row.date) },
  { key: 'customerName', header: 'Customer', cell: (row) => dash(row.customerName) },
  { key: 'saleDocNumber', header: 'Sale', cell: (row) => dash(row.saleDocNumber) },
  { key: 'amount', header: 'Amount', align: 'right', cell: (row) => money(row.amount) },
  { key: 'netAmount', header: 'Net', align: 'right', cell: (row) => money(row.netAmount) },
  { key: 'taxAmount', header: 'VAT', align: 'right', cell: (row) => money(row.taxAmount) },
  { key: 'againstDue', header: 'Off balance', align: 'right', cell: (row) => money(row.againstDue) },
  { key: 'refundAmount', header: 'Refunded', align: 'right', cell: (row) => money(row.refundAmount) },
  { key: 'refundMethod', header: 'Method', cell: (row) => (row.refundAmount > 0 ? dash(row.refundMethod) : '—') },
  { key: 'reason', header: 'Reason', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.reason) },
  { key: 'createdBy', header: 'By', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.createdBy) },
];

function CreditNotesTab({ filters, toolbar }: { filters: ReportFilters; toolbar: UseReportToolbar }) {
  const { profile } = usePortal();
  const layout = useReportLayout('credit-notes', NOTE_TOGGLEABLE);
  const columns = applyReportLayout(NOTE_COLUMNS, layout);
  const query = useQuery({
    queryKey: returnsWalletKeys.creditNotes(filters),
    queryFn: ({ signal }) => fetchCreditNotes(filters, signal),
  });
  const narrow = (report: CreditNotesReport) => {
    const rows = applyClientFilters(report.rows, {
      search: toolbar.search,
      filterRows: toolbar.filterRows,
      matchAny: toolbar.matchAny,
      fieldValue: (row, field) =>
        field === 'Customer'
          ? row.customerName
          : field === 'Sale'
            ? (row.saleDocNumber ?? '')
            : field === 'Reason'
              ? row.reason
              : field === 'Refund method'
                ? row.refundMethod
                : field === 'By'
                  ? row.createdBy
                  : undefined,
      searchValues: (row) => [row.noteNumber, row.customerName, row.saleDocNumber ?? '', row.reason, row.note],
    });
    const sum = (pick: (row: CreditNoteRow) => number) => rows.reduce((acc, row) => acc + pick(row), 0);
    return {
      rows,
      count: rows.length,
      totalAmount: sum((row) => row.amount),
      totalAgainstDue: sum((row) => row.againstDue),
      totalRefund: sum((row) => row.refundAmount),
    };
  };
  const noteTotals = (report: ReturnType<typeof narrow>) => ({
    amount: money(report.totalAmount),
    netAmount: money(report.rows.reduce((acc, row) => acc + row.netAmount, 0)),
    taxAmount: money(report.rows.reduce((acc, row) => acc + row.taxAmount, 0)),
    againstDue: money(report.totalAgainstDue),
    refundAmount: money(report.totalRefund),
  });
  // The rows on screen, also outside AsyncSection so Export sends exactly them.
  const shown = query.data ? narrow(query.data) : null;

  return (
    <>
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search note #, customer, sale or reason"
        />
        <ReportFiltersButton
          fields={NOTE_FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Refund method' ? 'Cash / Card' : field === 'By' ? 'staff name' : field.toLowerCase()
          }
        />
        <ReportColumnsButton layout={layout} labels={NOTE_LABELS} />
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} deselected={toolbar.filterActive} />
        {shown && shown.rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu
              onExport={(format: ExportFormat) =>
                downloadReportExport(
                  tabTable(
                    'Credit Notes',
                    exportSubtitle(filters, profile?.branches ?? []),
                    filters,
                    columns,
                    shown.rows,
                    noteTotals(shown),
                  ),
                  format,
                )
              }
            />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        select={narrow}
        isEmpty={(report) => report.rows.length === 0}
        emptyTitle="No credit notes in this window"
        skeleton={<TableSkeleton columns={12} />}
      >
        {(report) => (
          <>
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <KpiCard label="Credited" value={`${report.count} · ${money(report.totalAmount)}`} />
              <KpiCard label="Off balance" value={money(report.totalAgainstDue)} />
              <KpiCard label="Refunded" value={money(report.totalRefund)} />
            </div>
            <DataTable
              caption="Credit notes"
              layout={layout}
              columns={columns}
              rows={report.rows}
              rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
              totals={{ label: 'Total', cells: noteTotals(report) }}
            />
          </>
        )}
      </AsyncSection>
    </>
  );
}

// ---------------------------------------------------------------- Customer wallet

const WALLET_FILTER_FIELDS = ['Customer', 'Type', 'Method'] as const;

const BALANCE_TOGGLEABLE = ['phone', 'totalIn', 'used', 'returned', 'refund', 'balance'] as const;
const BALANCE_LABELS: Record<string, string> = {
  phone: 'Phone',
  totalIn: 'In',
  used: 'Used',
  returned: 'Returned',
  refund: 'Refund',
  balance: 'Balance',
};

const BALANCE_COLUMNS: Column<WalletBalanceRow>[] = [
  { key: 'customerName', header: 'Customer', pinned: true, cell: (row) => dash(row.customerName) },
  { key: 'phone', header: 'Phone', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.phone) },
  { key: 'totalIn', header: 'In', align: 'right', cell: (row) => money(row.totalIn) },
  { key: 'used', header: 'Used', align: 'right', cell: (row) => money(row.used) },
  { key: 'returned', header: 'Returned', align: 'right', cell: (row) => money(row.returned) },
  { key: 'refund', header: 'Refund', align: 'right', cell: (row) => money(row.refund) },
  {
    key: 'balance',
    header: 'Balance',
    align: 'right',
    cell: (row) => <span className="font-semibold">{money(row.balance)}</span>,
  },
];

/** Money in (In / Returned) reads green; money out of the wallet (Used / Refund) amber. */
const TYPE_TONE: Record<string, BadgeTone> = {
  In: 'success',
  Returned: 'success',
  Used: 'accent',
  Refund: 'warning',
};

const MOVEMENT_TOGGLEABLE = ['customerName', 'type', 'amount', 'method', 'reference', 'note', 'createdBy'] as const;
const MOVEMENT_LABELS: Record<string, string> = {
  customerName: 'Customer',
  type: 'Type',
  amount: 'Amount',
  method: 'Method',
  reference: 'Reference',
  note: 'Note',
  createdBy: 'By',
};

const MOVEMENT_COLUMNS: Column<WalletMovementRow>[] = [
  { key: 'date', header: 'Date', pinned: true, cell: (row) => formatDate(row.date) },
  { key: 'customerName', header: 'Customer', cell: (row) => dash(row.customerName) },
  {
    key: 'type',
    header: 'Type',
    cell: (row) => (
      <Badge tone={TYPE_TONE[row.type] ?? 'neutral'} dot>
        {row.type}
      </Badge>
    ),
  },
  { key: 'amount', header: 'Amount', align: 'right', cell: (row) => money(row.amount) },
  { key: 'method', header: 'Method', cell: (row) => dash(row.method) },
  { key: 'reference', header: 'Reference', cell: (row) => dash(row.reference) },
  { key: 'note', header: 'Note', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.note) },
  { key: 'createdBy', header: 'By', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.createdBy) },
];

function WalletTab({ filters, toolbar }: { filters: ReportFilters; toolbar: UseReportToolbar }) {
  const { profile } = usePortal();
  const [showSettled, setShowSettled] = useState(false);
  const balanceLayout = useReportLayout('wallet-balances', BALANCE_TOGGLEABLE);
  const movementLayout = useReportLayout('wallet-movements', MOVEMENT_TOGGLEABLE);
  const balanceColumns = applyReportLayout(BALANCE_COLUMNS, balanceLayout);
  const movementColumns = applyReportLayout(MOVEMENT_COLUMNS, movementLayout);
  const query = useQuery({
    queryKey: returnsWalletKeys.wallet(filters),
    queryFn: ({ signal }) => fetchWallet(filters, signal),
  });

  const filterOptions = { search: toolbar.search, filterRows: toolbar.filterRows, matchAny: toolbar.matchAny };

  return (
    <>
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search customer, reference or note"
        />
        <ReportFiltersButton
          fields={WALLET_FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Type' ? 'In / Used / Returned / Refund' : field === 'Method' ? 'Cash / Card' : 'customer'
          }
        />
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} deselected={toolbar.filterActive} />
      </ReportToolbar>
      <AsyncSection
        state={query}
        select={(report: WalletReport) => {
          const balances = applyClientFilters(report.balances, {
            ...filterOptions,
            // A balance row has no type/method of its own — those filters
            // narrow the movement list only.
            fieldValue: (row, field) => (field === 'Customer' ? row.customerName : undefined),
            searchValues: (row) => [row.customerName, row.phone],
          });
          const movements = applyClientFilters(report.movements, {
            ...filterOptions,
            fieldValue: (row, field) =>
              field === 'Customer'
                ? row.customerName
                : field === 'Type'
                  ? row.type
                  : field === 'Method'
                    ? row.method
                    : undefined,
            searchValues: (row) => [row.customerName, row.reference, row.note, row.type],
          });
          return {
            ...report,
            balances,
            movements,
            totalBalance: balances.reduce((acc, row) => acc + row.balance, 0),
          };
        }}
        isEmpty={(report) => report.balances.length === 0 && report.movements.length === 0}
        emptyTitle="No customer wallet activity"
        emptyDescription="No customer has paid money in advance up to this date."
        skeleton={<TableSkeleton columns={7} />}
      >
        {(report) => {
          const holding = report.balances.filter((row) => row.balance !== 0);
          const shown = showSettled ? report.balances : holding;
          const balanceTotals = {
            totalIn: money(shown.reduce((acc, row) => acc + row.totalIn, 0)),
            used: money(shown.reduce((acc, row) => acc + row.used, 0)),
            returned: money(shown.reduce((acc, row) => acc + row.returned, 0)),
            refund: money(shown.reduce((acc, row) => acc + row.refund, 0)),
            balance: money(shown.reduce((acc, row) => acc + row.balance, 0)),
          };
          const branches = profile?.branches ?? [];
          return (
            <>
              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <KpiCard label={`Held as of ${formatDate(report.asOf)}`} value={money(report.totalBalance)} />
                <KpiCard label="Customers holding a balance" value={String(holding.length)} />
                <KpiCard label="Movements in period" value={String(report.movements.length)} />
              </div>

              <section className="mb-8">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <h2 className="text-sm font-semibold text-slate-900">
                    Balances as of {formatDate(report.asOf)}
                  </h2>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={showSettled}
                        onChange={(event) => setShowSettled(event.target.checked)}
                      />
                      Show settled
                    </label>
                    <ReportColumnsButton layout={balanceLayout} labels={BALANCE_LABELS} />
                    {shown.length > 0 ? (
                      <ExportMenu
                        onExport={(format: ExportFormat) =>
                          downloadReportExport(
                            tabTable(
                              'Customer Wallet Balances',
                              exportSubtitle(filters, branches, [`Balances as of ${readableDate(report.asOf)}`]),
                              filters,
                              balanceColumns,
                              shown,
                              balanceTotals,
                              { balance: (row) => money(row.balance) },
                            ),
                            format,
                          )
                        }
                      />
                    ) : null}
                  </div>
                </div>
                {shown.length === 0 ? (
                  <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
                    No customer holds a balance.
                  </p>
                ) : (
                  <DataTable
                    caption="Customer wallet balances"
                    layout={balanceLayout}
                    columns={balanceColumns}
                    rows={shown}
                    rowKey={(row) => `${row.deviceId}-${row.customerSourceId}`}
                    totals={{ label: 'Total', cells: balanceTotals }}
                  />
                )}
              </section>

              <section>
                <div className="mb-3 flex items-center justify-between gap-4">
                  <h2 className="text-sm font-semibold text-slate-900">Movements</h2>
                  <div className="flex items-center gap-3">
                    <ReportColumnsButton layout={movementLayout} labels={MOVEMENT_LABELS} />
                    {report.movements.length > 0 ? (
                      <ExportMenu
                        onExport={(format: ExportFormat) =>
                          downloadReportExport(
                            tabTable(
                              'Customer Wallet Movements',
                              exportSubtitle(filters, branches),
                              filters,
                              movementColumns,
                              report.movements,
                              null,
                              { type: (row) => row.type },
                            ),
                            format,
                          )
                        }
                      />
                    ) : null}
                  </div>
                </div>
                {report.movements.length === 0 ? (
                  <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
                    No wallet movements in this window.
                  </p>
                ) : (
                  <DataTable
                    caption="Customer wallet movements"
                    layout={movementLayout}
                    columns={movementColumns}
                    rows={report.movements}
                    rowKey={(row, index) => `${row.date}-${row.reference}-${index}`}
                  />
                )}
              </section>
            </>
          );
        }}
      </AsyncSection>
    </>
  );
}

// ---------------------------------------------------------------- Page

/**
 * Money going back to customers: goods returned against a sale, credit
 * notes raised on a bill, and the advance wallet (money paid ahead and what
 * has been used, returned to it, or refunded out of it). Every endpoint
 * returns its whole set for the range, so search/filters run in the browser.
 */
export function ReturnsWalletPage() {
  const { profile } = usePortal();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const tabParam = params.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'returns';

  const selectTab = (next: Tab) => {
    if (next === tab) return;
    // Each tab has its own filter fields, so an applied filter or search
    // would not mean the same thing on the next one. One URL write, not a
    // setFilters + setParams pair, so neither update can drop the other.
    setParams((prev) => {
      const merged = new URLSearchParams(prev);
      for (const key of ['search', 'filters', 'matchAny']) merged.delete(key);
      merged.set('tab', next);
      return merged;
    });
  };

  return (
    <div>
      <PageHeader
        title="Returns, credit notes & wallet"
        description="Sales returns, credit notes, and money customers hold on account in advance."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <div className="mb-4 flex gap-1 border-b border-slate-200" role="tablist" aria-label="Report">
        {TABS.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            id={`returns-wallet-tab-${option.id}`}
            aria-selected={tab === option.id}
            aria-controls="returns-wallet-panel"
            onClick={() => selectTab(option.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === option.id
                ? 'border-brand-700 text-brand-700'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id="returns-wallet-panel" aria-labelledby={`returns-wallet-tab-${tab}`}>
        {tab === 'returns' ? <SalesReturnsTab filters={filters} toolbar={toolbar} /> : null}
        {tab === 'notes' ? <CreditNotesTab filters={filters} toolbar={toolbar} /> : null}
        {tab === 'wallet' ? <WalletTab filters={filters} toolbar={toolbar} /> : null}
      </div>
    </div>
  );
}
